import {
  AddressFragment,
  AddressInput,
  useEmployeeAddress,
  useUpdateAddress,
} from '@alice-financial/alice-sdk'
import { RestApiError } from '@alice-financial/api'
import * as React from 'react'
import { useForm } from 'react-hook-form'

export const addressToAddressInput = (
  address: AddressFragment | null | undefined,
  mustVerify: boolean = false
): AddressInput => {
  const { line1, line2, cityName, stateCode, zipcode, isVerified } = address ?? {}
  return {
    line1: line1 || '',
    line2: line2 || '',
    cityName: cityName || '',
    stateCode: stateCode || '',
    zipcode: zipcode || '',
    mustVerify: !isVerified && mustVerify,
  }
}

/**
 * Wrapper around mutation and response notification hooks to
 * expose only what the display component needs for triggering
 * form submissions - all non-rendering logic should be defined here
 *
 * This is a 'headless' hook
 *
 * Todo: avoid headlessness
 *
 * state:
 * - `contenteditable`: is the form in 'edit' mode?
 * - `isLoading`: loading initial data
 * - `isSubmitting`: the mutation is currently in-flight
 * - `control`: react-hook-form prop for individual field `Controller`
 * - `isSubmitDisabled`: what it says
 * - `onSubmit`: generic form `onSubmit` callback
 * - `onEdit`: generic callback to trigger edit mode
 * - `onCancel`: generic callback to cancel edit mode
 */
export const useHeadlessAddressEdit = (options?: { doVerify?: boolean }) => {
  const [contenteditable, setContenteditable] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [error, setError] = React.useState<RestApiError | null>(null)
  const { data: address, isLoading } = useEmployeeAddress()

  const userHasAddress = isLoading ? undefined : Boolean(address?.line1)
  const mustVerify = options?.doVerify && !address?.isVerified

  // set up the mutation hook with response status notifications
  const { mutate: updateAddress } = useUpdateAddress({
    onMutate: () => {
      setError(null)
      setIsSubmitting(true)
      setContenteditable(false)
    },
    onError: (error) => {
      setContenteditable(true)
      setError(error)
    },
    onSettled: () => setIsSubmitting(false),
  })

  // set up form state management
  const defaultValues = React.useMemo(
    () => addressToAddressInput(address, options?.doVerify),
    [address, options?.doVerify]
  )
  const { reset, handleSubmit, control } = useForm<AddressInput>({
    mode: 'all',
    defaultValues,
  })

  React.useEffect(() => {
    if (error) return
    reset(defaultValues)
  }, [error, defaultValues, reset])

  return {
    contenteditable: contenteditable || userHasAddress === false || mustVerify,
    isLoading,
    isSubmitting,
    error,
    control,
    isSubmitDisabled: isSubmitting,
    onSubmit: handleSubmit((values) => {
      values.mustVerify = options?.doVerify
      updateAddress(values)
    }),
    onEdit: () => setContenteditable(true),
    onCancel: () => {
      setContenteditable(false)
      reset()
    },
    address,
  }
}
